import { useEffect } from 'react';

/* ------------------------------------------------------------------
   Per-route <head> manager for the SPA.

   Social scrapers (Facebook, WhatsApp, iMessage) never run JS, so the
   static defaults in index.html serve them; this component keeps the
   *rendered* document — the one Google and Bing index — accurate per
   route: title, description, canonical, robots, OG mirrors, and
   structured data.
   ------------------------------------------------------------------ */

export const SITE_URL = 'https://labellatrading.ca';
export const SITE_NAME = 'La Bella Trading';
const DEFAULT_TITLE = 'La Bella Trading | Pure Honey, Olive Oil & Natural Soaps';
const DEFAULT_DESCRIPTION =
  'La Bella Trading — raw Mediterranean honey, rare bee products, extra virgin olive oil, and natural soaps. Family owned since 2003.';
const DEFAULT_IMAGE = `${SITE_URL}/assets/uploads/banner.jpg`;
const JSONLD_ID = 'seo-route-jsonld';

/** Collapse an HTML fragment into plain text sized for a meta description. */
export function seoText(html, max = 158) {
  const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
  const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
}

function upsertMeta(attr, key, content) {
  let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function upsertLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
}

export default function Seo({ title, description, path, image, type = 'website', noindex = false, jsonLd = null }) {
  useEffect(() => {
    const fullTitle = title
      ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`)
      : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const cleanPath = (path ?? window.location.pathname).split(/[?#]/)[0];
    const canonical = cleanPath === '/' ? `${SITE_URL}/` : `${SITE_URL}${cleanPath}`;
    const ogImage = image || DEFAULT_IMAGE;

    document.title = fullTitle;
    upsertMeta('name', 'description', desc);
    upsertMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');
    upsertLink('canonical', canonical);

    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', desc);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', desc);
    upsertMeta('name', 'twitter:image', ogImage);

    let script = document.getElementById(JSONLD_ID);
    if (jsonLd) {
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = JSONLD_ID;
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else if (script) {
      script.remove();
    }
    // Serialize jsonLd for the dep list: pages pass inline objects whose
    // identity changes every render even when the content hasn't.
  }, [title, description, path, image, type, noindex, JSON.stringify(jsonLd)]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
